import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isPrefersColorSchemeSupported, ThemeType } from 'common/theme';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { AuthResult, INativeAppState } from 'nativeAppShim';

import { getDefaultWebViewState, IWebViewState, NativeDialogArea } from './webViewState';

const initialState: IWebViewState = getDefaultWebViewState();

function getFontScale(nativeAppState: INativeAppState): string | undefined {
  try {
    // Note accessing fontScale would throw if it was not provided by clients
    return nativeAppState.fontScale || '1';
  } catch {
    // ignore if fontScale was not provided (back-compat)
    return undefined;
  }
}

function getSupportsLocalAuth(nativeAppState: INativeAppState): boolean {
  try {
    // Note accessing supportsLocalAuth would throw if it was not provided by clients
    return !!nativeAppState.supportsLocalAuth;
  } catch {
    // ignore if supportsLocalAuth was not provided (back-compat)
    return false;
  }
}

function getSupportsDarkMode(nativeAppState: INativeAppState): boolean {
  try {
    // Note accessing supportsDarkMode would throw if it was not provided by clients
    return !!nativeAppState.supportsDarkMode;
  } catch {
    // ignore if supportsDarkMode was not provided (back-compat)
    return false;
  }
}

export function setCustomFontSize(fontScale: string | undefined): number {
  const defaultScaleFactor = 1;
  if (!fontScale) {
    return defaultScaleFactor;
  }

  let fontScaleFactor = defaultScaleFactor;
  try {
    fontScaleFactor = parseFloat(fontScale);
    if (Number.isNaN(fontScaleFactor)) {
      fontScaleFactor = defaultScaleFactor;
      TelemetryProvider.Instance.trackException({ name: 'InvalidFontScale', message: `Font scale: ${fontScale}` });
    }

    // Min: 25%, Max: 500%
    fontScaleFactor = Math.min(Math.max(fontScaleFactor, 0.25), 5);

    const newFontSize = 62.5 * fontScaleFactor;
    document.getElementsByTagName('html')[0].style.fontSize = `${newFontSize}%`;
    TelemetryProvider.Instance.trackEvent('setCustomFontSize', { newFontSize });
  } catch {
    TelemetryProvider.Instance.trackException({ name: 'FontScalingFailed', message: `Font scale: ${fontScaleFactor}` });
  }

  return fontScaleFactor;
}

export const webViewSlice = createSlice({
  name: 'webView',
  initialState,
  reducers: {
    initializeNativeAppState: (state, action: PayloadAction<INativeAppState>) => {
      // Read the initial properties
      const { version, platform, locale, theme } = action.payload;

      // Read additional properties in try-catch to avoid breaking old clients
      const fontScaleFactor = setCustomFontSize(getFontScale(action.payload));
      const supportsLocalAuth = getSupportsLocalAuth(action.payload);
      const supportsDarkMode = getSupportsDarkMode(action.payload);

      TelemetryProvider.Instance.trackEvent('initializeNativeAppState', {
        version,
        platform,
        locale,
        fontScaleFactor,
        supportsLocalAuth,
        supportsDarkMode,
        supportsPrefersColorScheme: isPrefersColorSchemeSupported(),
        theme,
      });
      state.nativeAppState = {
        version,
        platform,
        locale,
        fontScale: fontScaleFactor.toString(),
        supportsLocalAuth,
        supportsDarkMode,
        theme,
      };
      state.initComplete = true;
    },
    setWebViewLocale: (state, action: PayloadAction<string>) => {
      if (state.nativeAppState) {
        state.nativeAppState.locale = action.payload;
      }
    },
    setNativeDialogArea: (state, action: PayloadAction<NativeDialogArea>) => {
      state.nativeDialogArea = action.payload;
    },
    setDialogClosing: (state, action: PayloadAction<boolean>) => {
      state.dialogClosing = action.payload;
    },
    setWebViewRendered: (state, action: PayloadAction<boolean>) => {
      state.isWebViewRendered = action.payload;
    },
    resetWebViewStates: (state) => {
      state.isPreviousDialog = false;
      state.componentKey = Date.now();
      state.isWebViewRendered = false;
    },
    setIsPreviousDialog: (state) => {
      state.isPreviousDialog = true;
    },
    setAuthResult: (state, action: PayloadAction<AuthResult>) => {
      state.authResult = action.payload;
    },
    setWebViewTheme: (state, action: PayloadAction<ThemeType>) => {
      if (state.nativeAppState) {
        state.nativeAppState.theme = action.payload;
      }
    },
  },
});

export const {
  setNativeDialogArea,
  initializeNativeAppState,
  setWebViewLocale,
  setDialogClosing,
  setWebViewRendered,
  resetWebViewStates,
  setIsPreviousDialog,
  setAuthResult,
  setWebViewTheme,
} = webViewSlice.actions;

export default webViewSlice.reducer;
